import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDownIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  CogIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

const FAQSection: React.FC = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);

  const faqs = [
    {
      id: 1,
      question: 'هل يمكنني تجربة المنصة مجانًا؟',
      answer: 'نعم، نوفر تجربة مجانية لمدة 14 يومًا بدون الحاجة لبطاقة ائتمانية. يمكنك الوصول إلى جميع الميزات الأساسية واستكشاف المنصة بالكامل قبل اتخاذ قرار الاشتراك.',
      icon: QuestionMarkCircleIcon,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 2,
      question: 'هل المنصة مرخصة ومتوافقة مع الأنظمة السعودية؟',
      answer: 'نعم، FLYN مرخصة ومتوافقة بالكامل مع تعليمات البنك المركزي السعودي (SAMA) ونظام Open Banking. كما أن تخزين البيانات يتبع قانون حماية البيانات الشخصية (PDPL) السعودي مع تشفير AES-256.',
      icon: ShieldCheckIcon,
      color: 'from-green-500 to-emerald-500'
    },
    {
      id: 3,
      question: 'هل تتكامل المنصة مع جميع البنوك السعودية؟',
      answer: 'نعم، تتكامل FLYN مع جميع البنوك المرتبطة بشبكة Open Banking السعودية، بما في ذلك البنك الأهلي، الراجحي، سامبا، البنك السعودي الفرنسي، وغيرها من البنوك المعتمدة.',
      icon: BanknotesIcon,
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 4,
      question: 'هل FLYN مناسبة للشركات الصغيرة والمتوسطة؟',
      answer: 'بالتأكيد، تم تصميم FLYN خصيصًا لاحتياجات الشركات الصغيرة والمتوسطة. نوفر خطط أسعار مرنة تبدأ من 299 ريال شهريًا، مع إمكانية التوسع حسب نمو شركتك.',
      icon: CogIcon,
      color: 'from-orange-500 to-red-500'
    },
    {
      id: 5,
      question: 'كيف يعمل التمويل الفوري في المنصة؟',
      answer: 'يحلل نظامنا الذكي تدفقاتك النقدية وتاريخ المعاملات لتقييم جدارتك الائتمانية. بعد التقييم، نعرض عليك خيارات تمويل من شركائنا المرخصين، ويمكن الحصول على التمويل خلال 6 ساعات فقط.',
      icon: BoltIcon,
      color: 'from-indigo-500 to-purple-500'
    },
    {
      id: 6,
      question: 'ما هي تكلفة استخدام المنصة؟',
      answer: 'نوفر خطط متنوعة تبدأ من 299 ريال شهريًا للخطة الأساسية، 599 ريال للخطة المتقدمة، و999 ريال للخطة الاحترافية. جميع الخطط تشمل الدعم الفني والتحديثات المجانية.',
      icon: BanknotesIcon,
      color: 'from-teal-500 to-green-500'
    },
    {
      id: 7,
      question: 'هل يمكن تخصيص المنصة حسب احتياجات شركتي؟',
      answer: 'نعم، توفر FLYN إمكانيات تخصيص واسعة. يمكنك إنشاء بطاقات ذكية مخصصة، تعديل لوحة التحكم، إضافة مؤشرات أداء خاصة بشركتك، وتكوين التنبيهات حسب احتياجاتك.',
      icon: CogIcon,
      color: 'from-pink-500 to-rose-500'
    },
    {
      id: 8,
      question: 'كيف يتم حماية بيانات شركتي؟',
      answer: 'نستخدم أعلى معايير الأمان بما في ذلك تشفير AES-256، بروتوكولات Zero-Trust، مراقبة الأمان على مدار الساعة، وامتثال كامل لقانون PDPL. بياناتك محمية ومخزنة في خوادم آمنة داخل السعودية.',
      icon: ShieldCheckIcon,
      color: 'from-emerald-500 to-teal-500'
    }
  ];

  const toggleFAQ = (id: number) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <section id="faq" className="py-20 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full glass-card text-sm font-medium text-primary-600 dark:text-primary-400 mb-6">
            <QuestionMarkCircleIcon className="w-4 h-4 ml-2 rtl:ml-0 rtl:mr-2" />
            الأسئلة الشائعة
          </div>
          
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            أجوبة لأهم أسئلتك
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            كل ما تحتاج معرفته عن FLYN وكيفية استخدامها لتطوير شركتك
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={faq.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="glass-card rounded-xl overflow-hidden border border-gray-200/50 dark:border-gray-700/50"
            >
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full px-6 py-6 text-right rtl:text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-200"
              >
                <div className="flex items-center space-x-4 rtl:space-x-reverse">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${faq.color} p-2 flex-shrink-0`}>
                    <faq.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {faq.question}
                  </h3>
                </div>
                <motion.div
                  animate={{ rotate: openFAQ === faq.id ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex-shrink-0 mr-4 rtl:mr-0 rtl:ml-4"
                >
                  <ChevronDownIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </motion.div>
              </button>

              <AnimatePresence>
                {openFAQ === faq.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 pt-0">
                      <div className="mr-14 rtl:mr-0 rtl:ml-14">
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Contact Support */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <div className="glass-card p-8 rounded-2xl">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              لم تجد إجابة لسؤالك؟
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              فريق الدعم الفني متاح على مدار الساعة لمساعدتك
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 bg-gradient-primary text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-300"
              >
                تواصل معنا
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg font-semibold hover:shadow-lg transition-all duration-300"
              >
                احجز مكالمة
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
